import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom';
import Slider from 'react-slick';
import DOMPurify from 'dompurify';
import publicApi from '../services/public.api';
import { useLocation } from '../context/LocationContext';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import InStoreOnlyModal from './InStoreOnlyModal';

const ProductDetailsOne = () => {
    const { id } = useParams();
    const { isCasaRegion, currentCity, priceRegion } = useLocation();
    const { addToCart } = useCart();
    const { settings: siteSettings } = useSiteSettings();
    const [showInStoreModal, setShowInStoreModal] = useState(false);

    // Kept for later use, but hidden on UI for now
    const SHOW_NUTRITION_VALUES = false;
    
    // Product state
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mainImage, setMainImage] = useState('');
    const [galleryImages, setGalleryImages] = useState([]);
    
    // Offer countdown state
    const [offerCountdown, setOfferCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [hasActiveOffer, setHasActiveOffer] = useState(false);
    
    // Reviews state
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0, distribution: [] });
    
    // Review form state
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewTitle, setReviewTitle] = useState('');
    const [reviewComment, setReviewComment] = useState('');
    const [submittingReview, setSubmittingReview] = useState(false);

    // Image zoom (inner zoom inside frame + lightbox)
    const [zoomHover, setZoomHover] = useState(false);
    const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxScale, setLightboxScale] = useState(1);

    const shippingRulesStyles = `
        .product-shipping-rules {
            margin: 0;
            padding-left: 0;
            list-style: none;
            display: grid;
            gap: 10px;
        }
        .product-shipping-rules li {
            position: relative;
            padding-left: 18px;
            line-height: 1.55;
        }
        .product-shipping-rules li::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0.55em;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #c5a05e;
            box-shadow: 0 0 0 3px rgba(197, 160, 94, 0.18);
        }
        .product-shipping-rules strong {
            color: #111827;
            font-weight: 700;
        }
        .product-image-zoom-wrap {
            position: relative;
            z-index: 1;
        }
        /* The template's default .product-details__thumb-slider has a huge
           responsive padding + fixed height that shrinks the actual clickable
           image area to a small box floating in the middle of the big card.
           Reset it here so the zoom icon + zoom preview use the FULL big card. */
        .product-image-zoom-wrap .product-details__thumb-slider {
            padding: 0 !important;
            height: auto !important;
            min-width: 0 !important;
            display: block !important;
        }
        .product-image-zoom {
            position: relative;
            width: 100%;
            min-height: 480px;
            overflow: hidden;
            border-radius: 16px;
            background: #fff;
            cursor: zoom-in;
            user-select: none;
        }
        .product-image-zoom__img {
            width: 100%;
            height: 100%;
            min-height: 480px;
            max-height: 640px;
            object-fit: contain;
            display: block;
            pointer-events: none;
            transition: opacity 0.15s ease;
        }
        .product-image-zoom-wrap.is-zooming .product-image-zoom__img {
            opacity: 0.25;
        }
        .product-image-zoom__hint {
            position: absolute;
            top: 14px;
            right: 14px;
            z-index: 2;
            width: 40px;
            height: 40px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.92);
            border: 1px solid #e5e7eb;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #1a1208;
            box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
            pointer-events: none;
        }
        /* Centered zoom preview — stays contained inside the card frame,
           never overflows outside the image/card boundaries */
        .product-image-zoom__center-result {
            position: absolute;
            top: 8%;
            left: 8%;
            right: 8%;
            bottom: 8%;
            border-radius: 14px;
            border: 1px solid rgba(197, 160, 94, 0.35);
            background-color: #fff;
            background-repeat: no-repeat;
            box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
            z-index: 3;
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            transition: opacity 0.18s ease, visibility 0.18s ease;
        }
        .product-image-zoom-wrap.is-zooming .product-image-zoom__center-result {
            opacity: 1;
            visibility: visible;
        }
        .product-image-lightbox {
            position: fixed;
            inset: 0;
            z-index: 1055;
            background: rgba(10, 7, 4, 0.88);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
        }
        .product-image-lightbox__inner {
            position: relative;
            max-width: min(960px, 100%);
            max-height: 90vh;
            overflow: auto;
            border-radius: 16px;
        }
        .product-image-lightbox__img {
            display: block;
            max-width: 100%;
            height: auto;
            margin: 0 auto;
            transform-origin: center center;
            transition: transform 0.2s ease;
            border-radius: 12px;
        }
        .product-image-lightbox__close,
        .product-image-lightbox__btn {
            position: absolute;
            z-index: 2;
            width: 44px;
            height: 44px;
            border: none;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.95);
            color: #1a1208;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
        }
        .product-image-lightbox__close {
            top: 16px;
            right: 16px;
        }
        .product-image-lightbox__controls {
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 10px;
            z-index: 2;
        }
        .product-image-lightbox__controls .product-image-lightbox__btn {
            position: static;
        }
        @media (max-width: 1199px) {
            .product-image-zoom-wrap.is-zooming .product-image-zoom__img {
                opacity: 1;
            }
            .product-image-zoom-wrap.is-zooming .product-image-zoom__center-result {
                opacity: 0;
                visibility: hidden;
            }
            .product-image-zoom {
                cursor: zoom-in;
            }
        }
        @media (max-width: 767px) {
            .product-image-zoom {
                min-height: 320px;
            }
            .product-image-zoom__img {
                min-height: 320px;
                max-height: 420px;
            }
        }
    `;
    
    // Quantity state
    const [quantity, setQuantity] = useState(1);
    const [addedToCart, setAddedToCart] = useState(false);
    const incrementQuantity = () => setQuantity(quantity + 1);
    const decrementQuantity = () => setQuantity(quantity > 1 ? quantity - 1 : quantity);

    // Weight Variants Configuration
    const WEIGHT_OPTIONS = [
        { label: '250g', value: 0.25 },
        { label: '500g', value: 0.5 },
        { label: '1kg', value: 1 },
        { label: '2kg', value: 2 },
        { label: '3kg', value: 3 },
        { label: '5kg', value: 5 },
    ];
    
    const [selectedWeights, setSelectedWeights] = useState([1]); // Default 1kg

    // Auto-normalize weight if quantity x weight matches a standard tab
    // Example: 500g x 2 becomes 1kg x 1
    useEffect(() => {
        if (!product?.has_variants) return;
        
        const currentSum = selectedWeights.reduce((a, b) => a + b, 0);
        const totalWeight = currentSum * quantity;
        
        // Find if totalWeight matches any standard weight option
        const matchedOption = WEIGHT_OPTIONS.find(opt => opt.value === totalWeight);
        
        if (matchedOption && (quantity > 1 || selectedWeights.length > 1)) {
            // Only normalize if it simplifies the selection
            setSelectedWeights([matchedOption.value]);
            setQuantity(1);
        }
    }, [quantity, selectedWeights, product?.has_variants]);

    // Slider only when many gallery images (flex layout otherwise — no extra space)
    const settingsThumbs = useMemo(() => ({
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: Math.min(4, galleryImages.length),
        slidesToScroll: 1,
        focusOnSelect: true,
    }), [galleryImages.length]);

    // Calculate countdown for offer
    const calculateCountdown = useCallback((endDate) => {
        const end = new Date(endDate);
        const now = new Date();
        const diff = end - now;

        if (diff <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
        }

        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((diff % (1000 * 60)) / 1000),
            expired: false,
        };
    }, []);

    // Fetch product data
    useEffect(() => {
        const fetchProduct = async () => {
            if (!id) {
                setLoading(false);
                return;
            }
            
            try {
                const response = await publicApi.getProduct(id, { region: priceRegion });
                if (response.success) {
                    setProduct(response.data);
                    
                    const toImageUrl = (path) => {
                        if (!path) return null;
                        return path.startsWith('assets/') ? path : publicApi.getImageUrl(path);
                    };

                    const main =
                        toImageUrl(response.data.main_image) ||
                        toImageUrl(response.data.images?.[0]) ||
                        'assets/images/thumbs/product-details-thumb1.png';

                    const seen = new Set();
                    const gallery = (response.data.gallery_images || [])
                        .map(toImageUrl)
                        .filter((url) => {
                            if (!url || seen.has(url)) return false;
                            seen.add(url);
                            return true;
                        });

                    setMainImage(main);
                    setGalleryImages(gallery);
                    
                    // Check for active offer
                    if (response.data.active_offer) {
                        setHasActiveOffer(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching product:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id, priceRegion]);

    // Fetch reviews
    useEffect(() => {
        const fetchReviews = async () => {
            if (!id) return;
            
            try {
                const response = await publicApi.getProductReviews(id);
                if (response.success) {
                    setReviews(response.data.reviews);
                    setReviewStats(response.data.stats);
                }
            } catch (error) {
                console.error('Error fetching reviews:', error);
            }
        };

        fetchReviews();
    }, [id]);

    // Update offer countdown every second
    useEffect(() => {
        if (!product?.active_offer?.end_date) return;

        const updateCountdown = () => {
            const countdown = calculateCountdown(product.active_offer.end_date);
            setOfferCountdown(countdown);
            if (countdown.expired) {
                setHasActiveOffer(false);
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [product, calculateCountdown]);

    // Get product image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return 'assets/images/thumbs/product-details-thumb1.png';
        if (imagePath.startsWith('assets/') || imagePath.startsWith('http')) return imagePath;
        return publicApi.getImageUrl(imagePath);
    };

    // Format price
    const formatPrice = (price) => parseFloat(price).toFixed(2);

    // Render stars
    const renderStars = (rating, size = 'text-15') => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <span key={i} className={`${size} fw-medium ${i <= rating ? 'text-warning-600' : 'text-gray-300'} d-flex`}>
                    <i className="ph-fill ph-star" />
                </span>
            );
        }
        return stars;
    };

    // Handle review submission
    const handleSubmitReview = async (e) => {
        e.preventDefault();
        // TODO: Implement with auth token
        alert('Vous devez être connecté pour laisser un avis');
    };

    // Handle like review
    const handleLikeReview = async (reviewId) => {
        try {
            await publicApi.likeReview(reviewId);
            // Refresh reviews
            const response = await publicApi.getProductReviews(id);
            if (response.success) {
                setReviews(response.data.reviews);
            }
        } catch (error) {
            console.error('Error liking review:', error);
        }
    };

    useEffect(() => {
        if (!lightboxOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setLightboxOpen(false);
                setLightboxScale(1);
            }
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [lightboxOpen]);

    useEffect(() => {
        setZoomHover(false);
        setZoomPos({ x: 50, y: 50 });
        setLightboxScale(1);
    }, [mainImage]);

    // Loading state
    if (loading) {
        return (
            <section className="product-details py-80">
                <div className="container container-lg">
                    <div className="row gy-4">
                        <div className="col-12 text-center py-80">
                            <div className="spinner-border text-main-600" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-16 text-gray-600">Chargement du produit...</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // No product found
    if (!product) {
        return (
            <section className="product-details py-80">
                <div className="container container-lg">
                    <div className="row gy-4">
                        <div className="col-12 text-center py-80">
                            <i className="ph ph-package text-gray-300" style={{ fontSize: '64px' }}></i>
                            <h5 className="mt-24 text-gray-600">Produit non trouvé</h5>
                            <Link to="/shop" className="btn btn-main rounded-pill mt-16">
                                Retour à la boutique
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // Get price based on location
    const getPrice = (product) => {
        if (!product) return 0;
        if (isCasaRegion && product.price_casa) {
            return parseFloat(product.price_casa);
        }
        return parseFloat(product.price);
    };

    // Calculate current price (with offer if active)
    const basePrice = getPrice(product);
    const currentPrice = hasActiveOffer && product.active_offer 
        ? product.active_offer.discounted_price 
        : basePrice;
    const originalPrice = hasActiveOffer && product.active_offer 
        ? basePrice 
        : product.original_price;

    // Handle WhatsApp Order Logic
    const handleWhatsAppOrder = (e) => {
        e.preventDefault();
        
        // Centralized WhatsApp number for all orders
        const whatsappNumber = '0664216420';
        
        // Prepare order details
        const totalWeightValue = product.has_variants 
            ? selectedWeights.reduce((sum, w) => sum + w, 0) * quantity 
            : null;
        
        const finalPrice = product.has_variants 
            ? (currentPrice * (selectedWeights.reduce((sum, w) => sum + w, 0)) * quantity).toFixed(2)
            : (currentPrice * quantity).toFixed(2);
            
        const cityName = currentCity?.name || (isCasaRegion ? 'Casablanca' : 'Fès');
        
        // Build weight string for message
        const weightString = product.has_variants 
            ? `*Poids:* ${totalWeightValue >= 1 ? `${totalWeightValue}kg` : `${totalWeightValue * 1000}g`}\n`
            : '';
        
        // Build the message with clean formatting (French)
        const message = `*Nouvelle commande - DelicesBenkirane.com* 🛍️\n\n` +
            `*Produit:* ${product.name}\n` +
            weightString +
            `*Quantité:* ${quantity}\n` +
            `*Prix Total:* ${finalPrice} DH\n` +
            `*Ville:* ${cityName}\n\n` +
            `Je souhaite commander ce produit depuis le site https://delicesbenkirane.com. Merci.`;
            
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodedMessage}`;
        
        // Open in new tab
        window.open(whatsappUrl, '_blank');
    };

    const toggleWeight = (val) => {
        if (selectedWeights.includes(val)) {
            if (selectedWeights.length > 1) {
                setSelectedWeights(selectedWeights.filter(w => w !== val));
            }
        } else {
            setSelectedWeights([...selectedWeights, val]);
        }
    };

    const totalWeightSelection = selectedWeights.reduce((a, b) => a + b, 0);
    const displayPrice = product.has_variants 
        ? currentPrice * totalWeightSelection * quantity 
        : currentPrice * quantity;

    const isInStoreOnly = Boolean(product?.category_in_store_only);

    const openInStoreModal = (e) => {
        e?.preventDefault?.();
        setShowInStoreModal(true);
    };

    const handleWhatsAppClick = (e) => {
        if (isInStoreOnly) {
            openInStoreModal(e);
            return;
        }
        handleWhatsAppOrder(e);
    };

    const handleAddToCartClick = (e) => {
        if (isInStoreOnly) {
            openInStoreModal(e);
            return;
        }
        addToCart(product, quantity);
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 1500);
    };

    const restrictedBtnStyle = isInStoreOnly
        ? { opacity: 0.55, cursor: 'not-allowed' }
        : undefined;

    const handleZoomMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setZoomPos({
            x: Math.max(0, Math.min(100, x)),
            y: Math.max(0, Math.min(100, y)),
        });
    };

    const openLightbox = () => {
        setLightboxScale(1);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setLightboxScale(1);
    };

    return (
        <section className="product-details py-80">
            <style>{shippingRulesStyles}</style>
            <InStoreOnlyModal
                show={showInStoreModal}
                onHide={() => setShowInStoreModal(false)}
                settings={siteSettings}
                isCasaRegion={isCasaRegion}
                productName={product.name}
            />
            {lightboxOpen && (
                <div
                    className="product-image-lightbox"
                    onClick={closeLightbox}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Aperçu agrandi du produit"
                >
                    <button
                        type="button"
                        className="product-image-lightbox__close"
                        onClick={closeLightbox}
                        aria-label="Fermer"
                    >
                        <i className="ph ph-x text-xl" />
                    </button>
                    <div className="product-image-lightbox__controls" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="product-image-lightbox__btn"
                            onClick={() => setLightboxScale((s) => Math.max(1, Number((s - 0.25).toFixed(2))))}
                            aria-label="Zoom arrière"
                        >
                            <i className="ph ph-magnifying-glass-minus text-xl" />
                        </button>
                        <button
                            type="button"
                            className="product-image-lightbox__btn"
                            onClick={() => setLightboxScale((s) => Math.min(3, Number((s + 0.25).toFixed(2))))}
                            aria-label="Zoom avant"
                        >
                            <i className="ph ph-magnifying-glass-plus text-xl" />
                        </button>
                    </div>
                    <div className="product-image-lightbox__inner" onClick={(e) => e.stopPropagation()}>
                        <img
                            className="product-image-lightbox__img"
                            src={mainImage}
                            alt={product.name}
                            style={{ transform: `scale(${lightboxScale})` }}
                            onError={(e) => {
                                e.target.src = 'assets/images/thumbs/product-details-thumb1.png';
                            }}
                        />
                    </div>
                </div>
            )}
            <div className="container container-lg">
                <div className="row gy-4">
                    <div className="col-lg-9">
                        <div className="row gy-4">
                            <div className="col-xl-6">
                                <div className="product-details__left">
                                    <div className={`product-image-zoom-wrap ${zoomHover ? 'is-zooming' : ''}`}>
                                        <div className="product-details__thumb-slider border border-gray-100 rounded-16">
                                            <div
                                                className="product-image-zoom"
                                                onMouseEnter={() => setZoomHover(true)}
                                                onMouseLeave={() => setZoomHover(false)}
                                                onMouseMove={handleZoomMove}
                                                onClick={openLightbox}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openLightbox();
                                                    }
                                                }}
                                                aria-label="Agrandir l'image du produit"
                                            >
                                                <span className="product-image-zoom__hint" aria-hidden="true">
                                                    <i className="ph ph-magnifying-glass-plus text-lg" />
                                                </span>
                                                <img
                                                    className="product-image-zoom__img"
                                                    src={mainImage}
                                                    alt={product.name}
                                                    onError={(e) => {
                                                        e.target.src = 'assets/images/thumbs/product-details-thumb1.png';
                                                    }}
                                                />
                                                <div
                                                    className="product-image-zoom__center-result"
                                                    style={{
                                                        backgroundImage: `url(${mainImage})`,
                                                        backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                                                        backgroundSize: '220%',
                                                    }}
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {galleryImages.length > 0 && (
                                        <div className="mt-24">
                                            <div className="product-details__images-slider">
                                                {galleryImages.length > 4 ? (
                                                    <Slider {...settingsThumbs}>
                                                        {galleryImages.map((image) => (
                                                            <div
                                                                className={`center max-w-120 max-h-120 h-100 flex-center border rounded-16 p-8 ${
                                                                    mainImage === image ? 'border-main-600' : 'border-gray-100'
                                                                }`}
                                                                key={image}
                                                                onClick={() => setMainImage(image)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <img
                                                                    className="thum"
                                                                    src={image}
                                                                    alt={product.name}
                                                                    onError={(e) => {
                                                                        e.target.src = 'assets/images/thumbs/product-details-thumb1.png';
                                                                    }}
                                                                />
                                                            </div>
                                                        ))}
                                                    </Slider>
                                                ) : (
                                                    <div className="d-flex flex-wrap gap-12">
                                                        {galleryImages.map((image) => (
                                                            <button
                                                                type="button"
                                                                key={image}
                                                                className={`center max-w-120 max-h-120 flex-center border rounded-16 p-8 bg-white ${
                                                                    mainImage === image ? 'border-main-600' : 'border-gray-100'
                                                                }`}
                                                                onClick={() => setMainImage(image)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <img
                                                                    className="thum"
                                                                    src={image}
                                                                    alt={product.name}
                                                                    onError={(e) => {
                                                                        e.target.src = 'assets/images/thumbs/product-details-thumb1.png';
                                                                    }}
                                                                />
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-xl-6">
                                <div className="product-details__content">
                                    <h5 className="mb-12">{product.name}</h5>
                                    <div className="flex-align flex-wrap gap-12">
                                        <div className="flex-align gap-12 flex-wrap">
                                            <div className="flex-align gap-8">
                                                {renderStars(Math.round(product.rating_average || 0))}
                                            </div>
                                            <span className="text-sm fw-medium text-neutral-600">
                                                {product.rating_average || 0} Note Moyenne
                                            </span>
                                            <span className="text-sm fw-medium text-gray-500">
                                                ({product.rating_count || 0})
                                            </span>
                                        </div>
                                        <span className="text-sm fw-medium text-gray-500">|</span>
                                        <span className="text-gray-900">
                                            {" "}
                                            <span className="text-gray-400">Réf:</span> {product.sku || 'N/A'}{" "}
                                        </span>
                                    </div>
                                    <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                    <p className="text-gray-700">
                                        Produit de qualité supérieure, fabriqué avec soin selon les traditions marocaines. 
                                        Idéal pour toutes vos occasions spéciales.
                                    </p>

                                    <div className="mt-32 flex-align flex-wrap gap-32">
                                        <div className="flex-align gap-8">
                                            <h4 className="mb-0">{formatPrice(displayPrice)} DH</h4>
                                            {originalPrice && !product.has_variants && (
                                                <span className="text-md text-gray-500">{formatPrice(originalPrice)} DH</span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleWhatsAppClick}
                                            className="btn btn-main rounded-pill product-whatsapp-btn"
                                            style={restrictedBtnStyle}
                                            aria-disabled={isInStoreOnly}
                                        >
                                            Commander sur WhatsApp
                                        </button>
                                    </div>

                                    {product.has_variants && (
                                        <div className="mt-32">
                                            <h6 className="text-md mb-16 fw-bold text-gray-800">Sélectionnez le format :</h6>
                                            <div className="d-flex w-100 gap-12 flex-wrap">
                                                {WEIGHT_OPTIONS.slice(0, 3).map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        onClick={() => toggleWeight(opt.value)}
                                                        className={`flex-grow-1 rounded-8 py-16 px-8 fs-15 fw-bold transition-all d-flex flex-column align-items-center justify-content-center position-relative overflow-hidden`}
                                                        style={{ 
                                                            width: '30%',
                                                            minHeight: '80px',
                                                            border: selectedWeights.includes(opt.value) 
                                                                ? '2px dotted #C5A021' 
                                                                : '1px dotted #e0e0e0',
                                                            backgroundColor: selectedWeights.includes(opt.value) 
                                                                ? '#FFFBEB' 
                                                                : '#ffffff',
                                                            color: selectedWeights.includes(opt.value) 
                                                                ? '#C5A021' 
                                                                : '#666',
                                                            boxShadow: selectedWeights.includes(opt.value) 
                                                                ? '0 4px 12px rgba(197, 160, 33, 0.1)' 
                                                                : 'none'
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '18px' }}>{opt.label}</span>
                                                        {selectedWeights.includes(opt.value) && (
                                                            <div className="position-absolute top-0 end-0 p-2">
                                                                <i className="ph-fill ph-check-circle text-gold" style={{ color: '#C5A021' }} />
                                                            </div>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="mt-16 text-sm text-gray-500 d-flex align-items-center gap-8">
                                                <i className="ph ph-info text-lg" />
                                                Le prix est calculé au prorata (Base: {formatPrice(currentPrice)} DH / kg)
                                            </p>
                                        </div>
                                    )}

                                    <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                    <div className="flex-align flex-wrap gap-16 product-countdown-box rounded-8 py-16 px-24">
                                        <div className="flex-align gap-16">
                                            <span className="text-sm product-special-offer-text">Offre Spéciale :</span>
                                        </div>
                                        <div className="countdown" id="countdown11">
                                            <ul className="countdown-list flex-align flex-wrap">
                                                <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border product-countdown-item p-0 flex-center">
                                                    {offerCountdown.days} <span className="days" />
                                                </li>
                                                <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border product-countdown-item p-0 flex-center">
                                                    {offerCountdown.hours}<span className="hours" />
                                                </li>
                                                <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border product-countdown-item p-0 flex-center">
                                                    {offerCountdown.minutes}<span className="minutes" />
                                                </li>
                                                <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border product-countdown-item p-0 flex-center">
                                                    {offerCountdown.seconds}<span className="seconds" />
                                                </li>
                                            </ul>
                                        </div>
                                        <span className="text-gray-900 text-xs">
                                            Reste jusqu'à la fin de l'offre
                                        </span>
                                    </div>
                                    <div className="mb-24">
                                        <div className="mt-32 flex-align gap-12 mb-16">
                                            <span className="w-32 h-32 bg-white flex-center rounded-circle text-main-600 box-shadow-xl product-stock-icon">
                                                <i className="ph-fill ph-lightning" />
                                            </span>
                                            <h6 className="text-md mb-0 fw-bold text-gray-900">
                                                {product.stock_percentage <= 20 
                                                    ? 'Le produit est presque épuisé' 
                                                    : 'Produit en stock'}
                                            </h6>
                                        </div>
                                        <div
                                            className="progress w-100 bg-gray-100 rounded-pill h-8"
                                            role="progressbar"
                                            aria-label="Stock"
                                            aria-valuenow={product.stock_percentage}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                        >
                                            <div
                                                className="progress-bar bg-main-two-600 rounded-pill product-stock-progress"
                                                style={{ width: `${product.stock_percentage}%` }}
                                            />
                                        </div>
                                        <span className="text-sm text-gray-700 mt-8">
                                            Disponible seulement : {product.stock}
                                        </span>
                                    </div>
                                    <span className="text-gray-900 d-block mb-8">Quantité :</span>
                                    <div className="flex-between gap-16 flex-wrap">
                                        <div className="flex-align flex-wrap gap-16">
                                            <div className="border border-gray-100 rounded-pill py-9 px-16 flex-align">
                                                <button onClick={decrementQuantity}
                                                    type="button"
                                                    className="quantity__minus p-4 text-gray-700 hover-text-main-600 flex-center"
                                                >
                                                    <i className="ph ph-minus" />
                                                </button>
                                                <input
                                                    type="number"
                                                    className="quantity__input border-0 text-center w-32"
                                                    value={quantity} readOnly
                                                />
                                                <button onClick={incrementQuantity}
                                                    type="button"
                                                    className="quantity__plus p-4 text-gray-700 hover-text-main-600 flex-center"
                                                >
                                                    <i className="ph ph-plus" />
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleAddToCartClick}
                                                className={`btn ${addedToCart && !isInStoreOnly ? 'btn-success' : 'btn-main'} rounded-pill flex-align d-inline-flex gap-8 px-48 product-add-cart-btn`}
                                                disabled={addedToCart && !isInStoreOnly}
                                                style={{ transition: 'all 0.3s ease', ...restrictedBtnStyle }}
                                                aria-disabled={isInStoreOnly}
                                            >
                                                {addedToCart ? (
                                                    <><i className="ph ph-check" /> Ajouté !</>
                                                ) : (
                                                    <><i className="ph ph-shopping-cart" /> Ajouter au Panier</>
                                                )}
                                            </button>
                                        </div>
                                        <div className="flex-align gap-12">
                                            <Link
                                                to="#"
                                                className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle product-action-icon"
                                            >
                                                <i className="ph ph-heart" />
                                            </Link>
                                            <Link
                                                to="#"
                                                className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle product-action-icon"
                                            >
                                                <i className="ph ph-shuffle" />
                                            </Link>
                                            <Link
                                                to="#"
                                                className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle product-action-icon"
                                            >
                                                <i className="ph ph-share-network" />
                                            </Link>
                                        </div>
                                    </div>
                                    <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                    <div className="flex-between gap-16 p-12 border border-dashed rounded-8 mb-16 product-coupon-box">
                                        <div className="flex-align gap-12">
                                            <button
                                                type="button"
                                                className="w-18 h-18 flex-center border border-gray-900 text-xs rounded-circle hover-bg-gray-100"
                                            >
                                                <i className="ph ph-plus" />
                                            </button>
                                            <span className="text-gray-900 fw-medium text-xs">
                                                Coupon fabricant. 3.00 DH de réduction sur 5
                                            </span>
                                        </div>
                                        <Link
                                            to="/cart"
                                            className="text-xs fw-semibold text-decoration-underline product-view-details-link"
                                        >
                                            Voir les Détails
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3">
                        <div className="product-details__sidebar border border-gray-100 rounded-16 overflow-hidden">
                            <div className="p-24">
                                <div className="flex-between product-vendor-box rounded-pill p-8">
                                    <div className="flex-align gap-8">
                                        <span className="w-44 h-44 bg-white rounded-circle flex-center text-2xl">
                                            <i className="ph ph-storefront" />
                                        </span>
                                        <span className="text-black">Délices</span>
                                    </div>
                                    <Link
                                        to="/shop"
                                        className="btn btn-white rounded-pill text-uppercase"
                                    >
                                        Voir la Boutique
                                    </Link>
                                </div>
                            </div>
                            <div className="p-24 product-sidebar-item d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white product-sidebar-icon rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-truck" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Livraison flexible & rapide</h6>
                                    <p className="text-gray-700 mb-8">
                                        Options claires et tarifs transparents.
                                    </p>
                                    <ul className="product-shipping-rules text-gray-700">
                                        <li>
                                            <strong>Maroc</strong> : Livraison rapide dans tout le Royaume au tarif unique de <strong>50 DH/carton</strong>.
                                        </li>
                                        <li>
                                            <strong>International</strong> : Expédition vers de nombreuses destinations à <strong>20 DH/kg</strong> (commande minimale : <strong>10 kg</strong>).
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="p-24 product-sidebar-item d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white product-sidebar-icon rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-arrow-u-up-left" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Qualité artisanale garantie</h6>
                                    <p className="text-gray-700">Produits soigneusement sélectionnés, préparés avec des ingrédients de qualité.</p>
                                </div>
                            </div>
                            <div className="p-24 product-sidebar-item d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white product-sidebar-icon rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-check-circle" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">
                                        Retrait disponible à la boutique
                                    </h6>
                                    <p className="text-gray-700">Généralement prêt en 24 heures</p>
                                </div>
                            </div>
                            <div className="p-24 product-sidebar-item d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white product-sidebar-icon rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-credit-card" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Paiement</h6>
                                    <p className="text-gray-700">
                                        Paiement à la réception des marchandises, Paiement par carte en magasin, 
                                        Google Pay, Carte en ligne.
                                    </p>
                                </div>
                            </div>
                            <div className="p-24 product-sidebar-item d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white product-sidebar-icon rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-check-circle" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Garantie</h6>
                                    <p className="text-gray-700">
                                        La loi sur la protection du consommateur ne prévoit pas le retour
                                        de ce produit de qualité appropriée.
                                    </p>
                                </div>
                            </div>
                            <div className="p-24 product-sidebar-item d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white product-sidebar-icon rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-package" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Emballage</h6>
                                    <p className="text-gray-700">
                                        Emballage soigné et sécurisé pour garantir la qualité du produit.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pt-80">
                    <div className="product-dContent border rounded-24">
                        <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
                            <ul
                                className="nav common-tab nav-pills mb-3"
                                id="pills-tab"
                                role="tablist"
                            >
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link active product-tab-btn"
                                        id="pills-description-tab"
                                        data-bs-toggle="pill"
                                        data-bs-target="#pills-description"
                                        type="button"
                                        role="tab"
                                        aria-controls="pills-description"
                                        aria-selected="true"
                                    >
                                        Description
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link product-tab-btn"
                                        id="pills-reviews-tab"
                                        data-bs-toggle="pill"
                                        data-bs-target="#pills-reviews"
                                        type="button"
                                        role="tab"
                                        aria-controls="pills-reviews"
                                        aria-selected="false"
                                    >
                                        Avis
                                    </button>
                                </li>
                            </ul>
                            <Link
                                to="#"
                                className="btn bg-color-one rounded-16 flex-align gap-8 text-main-600 hover-bg-main-600 hover-text-white product-satisfaction-btn"
                            >
                                <img src="assets/images/icon/satisfaction-icon.png" alt="" />
                                100% Satisfaction Garantie
                            </Link>
                        </div>
                        <div className="product-dContent__box">
                            <div className="tab-content" id="pills-tabContent">
                                <div
                                    className="tab-pane fade show active"
                                    id="pills-description"
                                    role="tabpanel"
                                    aria-labelledby="pills-description-tab"
                                    tabIndex={0}
                                >
                                    <div className="mb-40">
                                        <h6 className="mb-24">Description du Produit</h6>
                                        {/* Dynamic description from database */}
                                        {product.description ? (
                                            <div 
                                                className="product-description-content"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }}
                                            />
                                        ) : (
                                            <>
                                                <p>
                                                    Découvrez notre produit {product.name}, fabriqué avec passion selon 
                                                    les traditions marocaines séculaires. Chaque produit est soigneusement 
                                                    préparé pour vous offrir une expérience gustative exceptionnelle.
                                                </p>
                                                <p>
                                                    Notre engagement envers la qualité et l'authenticité se reflète dans 
                                                    chaque détail. Nous utilisons uniquement les meilleurs ingrédients 
                                                    naturels, sélectionnés avec soin pour garantir une saveur unique et 
                                                    un goût incomparable.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    <div className="mb-40">
                                        <h6 className="mb-24">Spécifications du Produit</h6>
                                        <ul className="mt-32 product-specs-list">
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle product-spec-icon">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    Type de Produit:
                                                    <span className="text-gray-500"> Produit Artisanal</span>
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    Nom du Produit:
                                                    <span className="text-gray-500">
                                                        {" "}
                                                        {product.name}{" "}
                                                    </span>
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    Marque:
                                                    <span className="text-gray-500"> Groupe Délices Benkirane</span>
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    Certifié:
                                                    <span className="text-gray-500"> Oui</span>
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    Taille/Poids:
                                                    <span className="text-gray-500"> {product.weight || '250g / 500g / 1kg'}</span>
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    Code Article:
                                                    <span className="text-gray-500"> {product.sku || 'N/A'}</span>
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    Ingrédients:
                                                    <span className="text-gray-500">
                                                        {" "}
                                                        Ingrédients naturels sélectionnés.
                                                    </span>
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                    {SHOW_NUTRITION_VALUES && (
                                        <div className="mb-40">
                                            <h6 className="mb-24">Valeurs Nutritionnelles</h6>
                                            <ul className="mt-32 product-nutrition-list">
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle product-nutrition-icon">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        {" "}
                                                        Matières grasses totales 10g 13%
                                                    </span>
                                                </li>
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        {" "}
                                                        Acides gras saturés 1.5g 7%
                                                    </span>
                                                </li>
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        {" "}
                                                        Cholestérol 0mg 0%
                                                    </span>
                                                </li>
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        {" "}
                                                        Sodium 170mg 7%
                                                    </span>
                                                </li>
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        {" "}
                                                        Potassium 350mg 6%
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                    <div className="mb-0">
                                        <h6 className="mb-24">Plus de Détails</h6>
                                        <ul className="mt-32 product-details-list">
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle product-details-icon">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-gray-500">
                                                    {" "}
                                                    Produit certifié et testé pour la qualité
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-gray-500">
                                                    {" "}
                                                    Emballage écologique et respectueux de l'environnement
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-gray-500">
                                                    {" "}
                                                    Conservation optimale pour préserver la fraîcheur
                                                </span>
                                            </li>
                                            <li className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-gray-500"> Style: Artisanal Traditionnel</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div
                                    className="tab-pane fade"
                                    id="pills-reviews"
                                    role="tabpanel"
                                    aria-labelledby="pills-reviews-tab"
                                    tabIndex={0}
                                >
                                    <div className="row g-4">
                                        <div className="col-lg-6">
                                            <h6 className="mb-24">Description du Produit</h6>
                                            {/* Dynamic Reviews List */}
                                            {reviews.length > 0 ? (
                                                reviews.map((review, index) => (
                                                    <div key={review.id} className={`d-flex align-items-start gap-24 ${index < reviews.length - 1 ? 'pb-44 border-bottom border-gray-100 mb-44' : ''}`}>
                                                        <img
                                                            src={review.user.avatar}
                                                            alt={review.user.name}
                                                            className="w-52 h-52 object-fit-cover rounded-circle flex-shrink-0"
                                                            onError={(e) => {
                                                                e.target.src = 'assets/images/thumbs/comment-img1.png';
                                                            }}
                                                        />
                                                        <div className="flex-grow-1">
                                                            <div className="flex-between align-items-start gap-8 ">
                                                                <div className="">
                                                                    <h6 className="mb-12 text-md">{review.user.name}</h6>
                                                                    <div className="flex-align gap-8">
                                                                        {renderStars(review.rating)}
                                                                    </div>
                                                                </div>
                                                                <span className="text-gray-800 text-xs">
                                                                    {review.time_ago}
                                                                </span>
                                                            </div>
                                                            {review.title && (
                                                                <h6 className="mb-14 text-md mt-24">{review.title}</h6>
                                                            )}
                                                            <p className="text-gray-700">
                                                                {review.comment}
                                                            </p>
                                                            <div className="flex-align gap-20 mt-44">
                                                                <button 
                                                                    className="flex-align gap-12 text-gray-700 hover-text-main-600"
                                                                    onClick={() => handleLikeReview(review.id)}
                                                                >
                                                                    <i className="ph-bold ph-thumbs-up" />
                                                                    J'aime {review.likes_count > 0 && `(${review.likes_count})`}
                                                                </button>
                                                                <Link
                                                                    to="#comment-form"
                                                                    className="flex-align gap-12 text-gray-700 hover-text-main-600"
                                                                >
                                                                    <i className="ph-bold ph-arrow-bend-up-left" />
                                                                    Répondre
                                                                </Link>
                                                            </div>
                                                            {/* Replies */}
                                                            {review.replies && review.replies.length > 0 && (
                                                                <div className="mt-24 ms-24 border-start border-gray-100 ps-24">
                                                                    {review.replies.map((reply) => (
                                                                        <div key={reply.id} className="d-flex align-items-start gap-16 mb-16">
                                                                            <img
                                                                                src={reply.user.avatar}
                                                                                alt={reply.user.name}
                                                                                className="w-40 h-40 object-fit-cover rounded-circle flex-shrink-0"
                                                                                onError={(e) => {
                                                                                    e.target.src = 'assets/images/thumbs/comment-img1.png';
                                                                                }}
                                                                            />
                                                                            <div>
                                                                                <h6 className="text-sm mb-4">{reply.user.name}</h6>
                                                                                <p className="text-gray-600 text-sm">{reply.comment}</p>
                                                                                <span className="text-xs text-gray-400">{reply.time_ago}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-40">
                                                    <i className="ph ph-chat-circle text-gray-300" style={{ fontSize: '48px' }}></i>
                                                    <p className="text-gray-500 mt-16">Aucun avis pour le moment. Soyez le premier à donner votre avis!</p>
                                                </div>
                                            )}
                                            
                                            <div className="mt-56" id="comment-form">
                                                <div className="">
                                                    <h6 className="mb-24">Écrire un Avis</h6>
                                                    <span className="text-heading mb-8">
                                                        Comment trouvez-vous ce produit ?
                                                    </span>
                                                    <div className="flex-align gap-8 mb-16">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <button 
                                                                key={star}
                                                                type="button"
                                                                onClick={() => setReviewRating(star)}
                                                                className={`text-15 fw-medium ${star <= reviewRating ? 'text-warning-600' : 'text-gray-300'} d-flex`}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                            >
                                                                <i className="ph-fill ph-star" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="mt-32">
                                                    <form onSubmit={handleSubmitReview}>
                                                        <div className="mb-32">
                                                            <label
                                                                htmlFor="title"
                                                                className="text-neutral-600 mb-8"
                                                            >
                                                                Titre de l'Avis
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="common-input rounded-8"
                                                                id="title"
                                                                placeholder="Excellent Produit"
                                                                value={reviewTitle}
                                                                onChange={(e) => setReviewTitle(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="mb-32">
                                                            <label
                                                                htmlFor="desc"
                                                                className="text-neutral-600 mb-8"
                                                            >
                                                                Contenu de l'Avis
                                                            </label>
                                                            <textarea
                                                                className="common-input rounded-8"
                                                                id="desc"
                                                                rows={4}
                                                                placeholder="Partagez votre expérience avec ce produit..."
                                                                value={reviewComment}
                                                                onChange={(e) => setReviewComment(e.target.value)}
                                                            />
                                                        </div>
                                                        <button
                                                            type="submit"
                                                            className="btn btn-main rounded-pill mt-48 product-submit-review-btn"
                                                            disabled={submittingReview}
                                                        >
                                                            {submittingReview ? 'Envoi en cours...' : 'Soumettre l\'Avis'}
                                                        </button>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="ms-xxl-5">
                                                <h6 className="mb-24">Avis des Clients</h6>
                                                <div className="d-flex flex-wrap gap-44">
                                                    <div className="border border-gray-100 rounded-8 px-40 py-52 flex-center flex-column flex-shrink-0 text-center">
                                                        <h2 className="mb-6 text-main-600 product-rating-score">{reviewStats.average_rating}</h2>
                                                        <div className="flex-center gap-8">
                                                            {renderStars(Math.round(reviewStats.average_rating))}
                                                        </div>
                                                        <span className="mt-16 text-gray-500">
                                                            Note Moyenne du Produit
                                                        </span>
                                                    </div>
                                                    <div className="border border-gray-100 rounded-8 px-24 py-40 flex-grow-1">
                                                        {reviewStats.distribution.map((item) => (
                                                            <div key={item.rating} className="flex-align gap-8 mb-20">
                                                                <span className="text-gray-900 flex-shrink-0">{item.rating}</span>
                                                                <div
                                                                    className="progress w-100 bg-gray-100 rounded-pill h-8"
                                                                    role="progressbar"
                                                                    aria-label={`${item.rating} stars`}
                                                                    aria-valuenow={item.percentage}
                                                                    aria-valuemin={0}
                                                                    aria-valuemax={100}
                                                                >
                                                                    <div
                                                                        className="progress-bar bg-main-600 rounded-pill product-review-progress"
                                                                        style={{ width: `${item.percentage}%` }}
                                                                    />
                                                                </div>
                                                                <div className="flex-align gap-4">
                                                                    {renderStars(item.rating, 'text-xs')}
                                                                </div>
                                                                <span className="text-gray-900 flex-shrink-0">
                                                                    {item.count}
                                                                </span>
                                                            </div>
                                                        ))}
                                                        {reviewStats.distribution.length === 0 && (
                                                            <div className="text-center py-20">
                                                                <p className="text-gray-500 text-sm">Aucune statistique disponible</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ProductDetailsOne
